import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { paymentTrackingService } from '../../services/financial/payment-tracking';
import { invoicesService } from '../../services/accounting/invoices';
import { 
  XMarkIcon, 
  CheckIcon, 
  BanknotesIcon, 
  ClockIcon, 
  DocumentTextIcon, 
  PlusIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const PaymentTrackingModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('aging');
  
  // Data states
  const [agingReport, setAgingReport] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [collectionTasks, setCollectionTasks] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form states
  const [paymentForm, setPaymentForm] = useState({
    invoiceId: '',
    amount: '',
    paymentMethod: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });
  
  const [newTask, setNewTask] = useState({
    invoiceId: '',
    taskType: 'follow_up',
    priority: 'medium',
    dueDate: new Date().toISOString().split('T')[0],
    description: '',
    assignedTo: user?.email || ''
  });

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!currentCompany?.id) return;
      
      try {
        setLoading(true);
        
        // Load data with individual error handling
        const results = await Promise.allSettled([
          paymentTrackingService.generateAgingReport(currentCompany.id),
          invoicesService.getInvoices(currentCompany.id),
          paymentTrackingService.getCollectionTasks(currentCompany.id)
        ]);
        
        // Handle aging report result
        if (results[0].status === 'fulfilled') {
          setAgingReport(results[0].value);
        } else {
          console.error('Error loading aging report:', results[0].reason);
          setAgingReport({ agingBuckets: {}, totalOutstanding: 0, totalInvoices: 0 });
        }
        
        // Handle invoices result
        if (results[1].status === 'fulfilled') {
          setInvoices(results[1].value);
        } else {
          console.error('Error loading invoices:', results[1].reason);
          setInvoices([]);
        }
        
        // Handle collection tasks result
        if (results[2].status === 'fulfilled') {
          setCollectionTasks(results[2].value);
        } else {
          console.error('Error loading collection tasks:', results[2].reason);
          setCollectionTasks([]);
        }
        
        // Check if any critical error occurred
        const failedCount = results.filter(r => r.status === 'rejected').length;
        if (failedCount === results.length) {
          throw new Error('All data loading failed');
        } else if (failedCount > 0) {
          toast.error(t('payment_tracking.partial_data_load', 'Some data could not be loaded. Please refresh to try again.'));
        }
        
      } catch (error) {
        console.error('Error loading payment tracking data:', error);
        
        // More specific error handling
        if (error.code === 'permission-denied') {
          toast.error(t('payment_tracking.permission_denied', 'You don\'t have permission to access payment data'));
        } else if (error.code === 'unavailable') {
          toast.error(t('payment_tracking.service_unavailable', 'Payment service is temporarily unavailable'));
        } else {
          toast.error(t('payment_tracking.error_loading_data', 'Failed to load payment tracking data'));
        }
        
        // Set empty defaults
        setAgingReport({ agingBuckets: {}, totalOutstanding: 0, totalInvoices: 0 });
        setInvoices([]);
        setCollectionTasks([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentCompany?.id, t]);

  // Helper functions
  const getAgingCategoryDisplay = (daysPastDue) => {
    if (daysPastDue <= 30) return { label: t('payment_tracking.current_status', 'Current'), color: 'green' };
    if (daysPastDue <= 60) return { label: t('payment_tracking.medium_status', 'Medium'), color: 'yellow' };
    if (daysPastDue <= 90) return { label: t('payment_tracking.high_status', 'High'), color: 'orange' };
    return { label: t('payment_tracking.critical_status', 'Critical'), color: 'red' };
  };

  const getTaskPriorityDisplay = (priority) => {
    const displays = {
      low: { label: 'Low', color: 'green' },
      medium: { label: 'Medium', color: 'yellow' },
      high: { label: 'High', color: 'orange' },
      urgent: { label: 'Urgent', color: 'red' }
    };
    return displays[priority] || displays.medium;
  };

  // Event handlers
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.invoiceId || !paymentForm.amount) {
      toast.error(t('payment_tracking.fill_required_fields', 'Please fill all required fields'));
      return;
    }

    try {
      setLoading(true);
      await paymentTrackingService.recordPayment({
        ...paymentForm,
        companyId: currentCompany.id,
        recordedBy: user.uid,
        amount: parseFloat(paymentForm.amount)
      });
      
      toast.success(t('payment_tracking.payment_recorded', 'Payment recorded successfully'));
      setPaymentForm({
        invoiceId: '',
        amount: '',
        paymentMethod: 'cash',
        paymentDate: new Date().toISOString().split('T')[0],
        reference: '',
        notes: ''
      });
      onSuccess?.();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(t('payment_tracking.error_recording_payment', 'Failed to record payment'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.invoiceId || !newTask.description) {
      toast.error(t('payment_tracking.fill_required_fields', 'Please fill all required fields'));
      return;
    }

    try {
      setLoading(true);
      await paymentTrackingService.createCollectionTask({
        ...newTask,
        companyId: currentCompany.id,
        createdBy: user.uid,
        status: 'pending'
      });
      
      toast.success(t('payment_tracking.task_created', 'Collection task created successfully'));
      setNewTask({
        invoiceId: '',
        taskType: 'follow_up',
        priority: 'medium',
        dueDate: new Date().toISOString().split('T')[0],
        description: '',
        assignedTo: user?.email || ''
      });
      
      // Reload tasks
      const tasksData = await paymentTrackingService.getCollectionTasks(currentCompany.id);
      setCollectionTasks(tasksData);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('payment_tracking.error_creating_task', 'Failed to create task'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      setLoading(true);
      await paymentTrackingService.completeCollectionTask(taskId, user.uid);
      toast.success(t('payment_tracking.task_completed', 'Task marked as completed'));
      
      // Reload tasks
      const tasksData = await paymentTrackingService.getCollectionTasks(currentCompany.id);
      setCollectionTasks(tasksData);
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(t('payment_tracking.error_completing_task', 'Failed to complete task'));
    } finally {
      setLoading(false);
    }
  };



  // Calculate totals for aging report
  const totals = agingReport ? {
    totalAmount: Object.values(agingReport.agingBuckets).reduce((sum, bucket) => sum + bucket.amount, 0),
    totalCount: Object.values(agingReport.agingBuckets).reduce((sum, bucket) => sum + bucket.count, 0)
  } : { totalAmount: 0, totalCount: 0 };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('payment_tracking.title', 'Payment Tracking')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <LoadingSpinner />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {[
              { id: 'aging', label: t('payment_tracking.aging_report', 'Aging Report'), icon: ClockIcon },
              { id: 'payment', label: t('payment_tracking.record_payment', 'Record Payment'), icon: BanknotesIcon },
              { id: 'collections', label: t('payment_tracking.collections', 'Collections'), icon: DocumentTextIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Aging Report Tab */}
          {activeTab === 'aging' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t('payment_tracking.aging_report', 'Aging Report')}
                </h3>
                <div className="text-sm text-gray-600">
                  {t('payment_tracking.total_outstanding', 'Total Outstanding')}: {' '}
                  <span className="font-medium">{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(totals.totalAmount * 100) / 100)} SAR</span>
                </div>
              </div>
              
              {agingReport && (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(agingReport.agingBuckets).map(([key, bucket]) => (
                      <div key={key} className="p-4 border rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(bucket.amount * 100) / 100)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {bucket.label === 'days 0-30' && t('payment_tracking.days_0_30', 'days 0-30')}
                          {bucket.label === 'days 31-60' && t('payment_tracking.days_31_60', 'days 31-60')}
                          {bucket.label === 'days 61-90' && t('payment_tracking.days_61_90', 'days 61-90')}
                          {bucket.label === 'days +90' && t('payment_tracking.days_90_plus', 'days +90')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {bucket.count} {t('payment_tracking.invoices_count', 'invoices')}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('payment_tracking.invoice', 'Invoice')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('payment_tracking.customer', 'Customer')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('payment_tracking.amount', t('common.amount'))}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('payment_tracking.days_overdue', 'Days Overdue')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('payment_tracking.status', t('common.status'))}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('payment_tracking.actions', t('common.actions'))}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.values(agingReport.agingBuckets).flatMap(bucket => 
                          bucket.invoices.map(invoice => {
                            const category = getAgingCategoryDisplay(invoice.daysPastDue);
                            return (
                              <tr key={invoice.id}>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {invoice.invoiceNumber}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {invoice.clientName || invoice.client?.name || t('common.unknown', 'Unknown')}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(invoice.outstandingAmount * 100) / 100)} SAR
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {invoice.daysPastDue}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${category.color}-100 text-${category.color}-800`}>
                                    {category.label}
                                  </span>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => setSelectedInvoice(invoice)}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    <DocumentTextIcon className="h-4 w-4 inline mr-1" />
                                    {t('payment_tracking.view_details', t('common.view'))}
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                        {totals.totalCount === 0 && (
                          <tr>
                            <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                              <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                              <p className="text-lg font-medium text-gray-900 mb-2">
                                {t('payment_tracking.no_overdue_invoices', 'No overdue invoices')}
                              </p>
                              <p className="text-sm text-gray-500">
                                {t('payment_tracking.all_invoices_current', 'All invoices are current or paid')}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Invoice Details Modal */}
              {selectedInvoice && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium">
                        {t('payment_tracking.invoice_details', 'Invoice Details')}
                      </h3>
                      <button
                        onClick={() => setSelectedInvoice(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div><strong>{t('payment_tracking.invoice_number', 'Invoice #')}:</strong> {selectedInvoice.invoiceNumber}</div>
                      <div><strong>{t('payment_tracking.customer', 'Customer')}:</strong> {selectedInvoice.clientName || selectedInvoice.client?.name || t('common.unknown', 'Unknown')}</div>
                      <div><strong>{t('payment_tracking.amount', t('common.amount'))}:</strong> {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(selectedInvoice.outstandingAmount * 100) / 100)} SAR</div>
                      <div><strong>{t('payment_tracking.due_date', 'Due Date')}:</strong> {selectedInvoice.dueDate?.toDate?.()?.toLocaleDateString() || 'N/A'}</div>
                    </div>
                    <div className="mt-6 flex space-x-3">
                      <button
                        onClick={() => {
                          setPaymentForm(prev => ({ ...prev, invoiceId: selectedInvoice.id }));
                          setActiveTab('payment');
                          setSelectedInvoice(null);
                        }}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        {t('payment_tracking.record_payment', 'Record Payment')}
                      </button>
                      <button
                        onClick={() => setSelectedInvoice(null)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {t('common.close', t('common.close'))}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Record Payment Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">
                {t('payment_tracking.record_payment', 'Record Payment')}
              </h3>
              
              <div className="max-w-lg">
                <form onSubmit={handleRecordPayment}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('payment_tracking.select_invoice', 'Select Invoice')} *
                      </label>
                      <select
                        value={paymentForm.invoiceId}
                        onChange={(e) => {
                          const selectedInvoice = invoices.find(inv => inv.id === e.target.value);
                          setPaymentForm(prev => ({ 
                            ...prev, 
                            invoiceId: e.target.value,
                            amount: selectedInvoice ? Math.round(selectedInvoice.remainingAmount * 100) / 100 : ''
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">{t('payment_tracking.select_invoice_placeholder', 'Select an invoice...')}</option>
                        {invoices.filter(inv => inv.paymentStatus !== 'paid').map(invoice => (
                          <option key={invoice.id} value={invoice.id}>
                            {invoice.invoiceNumber || invoice.id} - {invoice.clientName} - {t('payment_tracking.total', t('common.total'))}: SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(invoice.totalAmount * 100) / 100)} - {t('payment_tracking.remaining', 'Remaining')}: SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(invoice.remainingAmount * 100) / 100)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Selected Invoice Details */}
                    {paymentForm.invoiceId && (() => {
                      const selectedInvoice = invoices.find(inv => inv.id === paymentForm.invoiceId);
                      if (!selectedInvoice) return null;
                      
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-2">
                            {t('payment_tracking.invoice_details', 'Invoice Details')}
                          </h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('payment_tracking.invoice_number', 'Invoice')}:</span>
                              <span className="font-medium">{selectedInvoice.invoiceNumber || selectedInvoice.id}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('payment_tracking.customer', 'Customer')}:</span>
                              <span className="font-medium">{selectedInvoice.clientName}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('payment_tracking.total_amount', 'Total Amount')}:</span>
                              <span className="font-medium">SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(selectedInvoice.totalAmount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('payment_tracking.paid_amount', 'Paid Amount')}:</span>
                              <span className="font-medium">SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(selectedInvoice.paidAmount)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-1">
                              <span className="text-gray-600 font-medium">{t('payment_tracking.remaining_amount', 'Remaining Amount')}:</span>
                              <span className="font-medium text-red-600">SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(selectedInvoice.remainingAmount * 100) / 100)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('payment_tracking.status', t('common.status'))}:</span>
                              <span className="font-medium">
                                {selectedInvoice.paymentStatus === 'partial' ? 
                                  t('invoices.status.partial', 'Partially Paid') : 
                                  selectedInvoice.paymentStatus === 'paid' ? 
                                  t('invoices.status.paid', t('common.paid')) :
                                  t('invoices.status.pending', t('common.pending'))
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('payment_tracking.amount', t('common.amount'))} *
                      </label>
                      <input
                        type="number"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('payment_tracking.payment_method', 'Payment Method')} *
                      </label>
                      <select
                        value={paymentForm.paymentMethod}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="cash">{t('payment_tracking.cash', 'Cash')}</option>
                        <option value="bank_transfer">{t('payment_tracking.bank_transfer', 'Bank Transfer')}</option>
                        <option value="check">{t('payment_tracking.check', 'Check')}</option>
                        <option value="credit_card">{t('payment_tracking.credit_card', 'Credit Card')}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('payment_tracking.payment_date', 'Payment Date')} *
                      </label>
                      <input
                        type="date"
                        value={paymentForm.paymentDate}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('payment_tracking.reference', 'Reference/Transaction #')}
                      </label>
                      <input
                        type="text"
                        value={paymentForm.reference}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('payment_tracking.notes', t('common.notes'))}
                      </label>
                      <textarea
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!paymentForm.invoiceId || !paymentForm.amount}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BanknotesIcon className="h-4 w-4 inline mr-1" />
                      {t('payment_tracking.record_payment', 'Record Payment')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Collections Tab */}
          {activeTab === 'collections' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">
                {t('payment_tracking.collection_management', 'Collection Management')}
              </h3>
              
              {/* Create New Task */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <h4 className="font-medium mb-4">
                  {t('payment_tracking.create_collection_task', 'Create Collection Task')}
                </h4>
                <form onSubmit={handleCreateTask} className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment_tracking.select_invoice', 'Select Invoice')} *
                    </label>
                    <select
                      value={newTask.invoiceId}
                      onChange={(e) => setNewTask(prev => ({ ...prev, invoiceId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                                              <option value="">{t('payment_tracking.select_invoice_placeholder', 'Select an invoice...')}</option>
                        {invoices.filter(inv => inv.paymentStatus !== 'paid').map(invoice => (
                          <option key={invoice.id} value={invoice.id}>
                            {invoice.invoiceNumber || invoice.id} - {invoice.clientName} - {t('payment_tracking.remaining', 'Remaining')}: SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.round(invoice.remainingAmount * 100) / 100)}
                          </option>
                        ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment_tracking.task_type', 'Task Type')} *
                    </label>
                    <select
                      value={newTask.taskType}
                      onChange={(e) => setNewTask(prev => ({ ...prev, taskType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="follow_up">{t('payment_tracking.follow_up', 'Follow Up')}</option>
                      <option value="phone_call">{t('accounting.phone_call')}</option>
                      <option value="email">{t('accounting.email')}</option>
                      <option value="meeting">{t('payment_tracking.meeting', 'Meeting')}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment_tracking.priority', 'Priority')} *
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="low">{t('accounting.low')}</option>
                      <option value="medium">{t('accounting.medium')}</option>
                      <option value="high">{t('accounting.high')}</option>
                      <option value="urgent">{t('payment_tracking.urgent', 'Urgent')}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment_tracking.due_date', 'Due Date')} *
                    </label>
                    <input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment_tracking.description', t('common.description'))} *
                    </label>
                    <textarea
                      value={newTask.description}
                      onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <button
                      type="submit"
                      disabled={!newTask.invoiceId || !newTask.description}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="h-4 w-4 inline mr-1" />
                      {t('payment_tracking.create_task', 'Create Task')}
                    </button>
                  </div>
                </form>
              </div>
              
              {/* Tasks List */}
              <div>
                <h4 className="font-medium mb-3">
                  {t('payment_tracking.active_tasks', 'Active Collection Tasks')}
                </h4>
                <div className="space-y-2">
                  {collectionTasks.filter(task => task.status === 'pending').map(task => {
                    const priority = getTaskPriorityDisplay(task.priority);
                    return (
                      <div key={task.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${priority.color}-100 text-${priority.color}-800`}>
                                {priority.label}
                              </span>
                              <span className="text-sm text-gray-500">{task.taskType}</span>
                            </div>
                            <div className="font-medium">{task.description}</div>
                            <div className="text-sm text-gray-600">
                              {t('payment_tracking.due', 'Due')}: {new Date(task.dueDate).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleCompleteTask(task.id)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                            >
                              <CheckIcon className="h-4 w-4 inline mr-1" />
                              {t('payment_tracking.complete', 'Complete')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {collectionTasks.filter(task => task.status === 'pending').length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      {t('payment_tracking.no_active_tasks', 'No active collection tasks')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentTrackingModal; 